import React from 'react';
import { CheckCircle, Package, MapPin, CreditCard, Phone, Mail, FileText, Download, Truck, Tag, List, Clock } from 'lucide-react';
import { useVocab, useIsAppointments } from './hooks/useVocab';

export const OrderConfirmation = ({ orderData, onClose }) => {
  const t = useVocab();
  const isAppt = useIsAppointments();
  if (!orderData) return null;

  // Normalize / derive fields to handle varying backend key conventions
  const customer = orderData.customer || {};
  const orderId = orderData.order_id || orderData.orderId || orderData.id || '—';
  // Add nested customer fallbacks (API may return customer: { name, phone, email })
  const customerName = orderData.customer_name || orderData.customerName || orderData.name || customer.name || '';
  const customerPhone = orderData.customer_phone || orderData.phone || orderData.customerPhone || customer.phone || '';
  const customerEmail = orderData.customer_email || orderData.email || orderData.customerEmail || customer.email || '';
  const paymentMethod = orderData.payment_method || orderData.paymentMethod || orderData.payment || '—';

  // Selected pickup/delivery time slot (set during the TimeSlotSelection step)
  const timeSlot = orderData.time_slot || null;
  const timeSlotDisplay = (() => {
    if (!timeSlot?.enabled || !timeSlot?.slotId) return '';
    const datePart = timeSlot.dateLabel || timeSlot.date || '';
    const timePart = timeSlot.startTime && timeSlot.endTime
      ? `${timeSlot.startTime} - ${timeSlot.endTime}`
      : (timeSlot.slotLabel || '');
    return [datePart, timePart].filter(Boolean).join(' · ');
  })();
  const items = orderData.items || orderData.order_items || orderData.products || orderData.cart || [];

  // Delivery method & destination fallbacks
  const deliveryMethod = orderData.delivery_method || orderData.deliveryMethod || orderData.shipping_method || orderData.fulfillment_type || '';
  const deliveryFee = orderData.delivery_fee ?? orderData.shipping_fee ?? orderData.shippingCost ?? orderData.deliveryCost ?? 0;
  const deliveryAddressObj = orderData.delivery_address || orderData.address || orderData.shipping_address || {};
  const deliveryAddressString = typeof deliveryAddressObj === 'string'
    ? deliveryAddressObj
    : [deliveryAddressObj.street, deliveryAddressObj.city, deliveryAddressObj.zip, deliveryAddressObj.notes]
      .filter(Boolean)
      .join(', ');
  const destination = deliveryAddressString || orderData.city || orderData.destination || '';
  // Derive delivery method display & whether it's pickup vs home
  const rawMethod = (deliveryMethod || '').toLowerCase();
  const isOnPremise = rawMethod === 'takeaway' || rawMethod === 'eat_in';
  const isPickupPoint = !isOnPremise && (['point', 'pickup', 'pickup_point', 'collection', 'locker'].some(k => rawMethod.includes(k)) || Boolean(orderData.delivery_point_name));
  const isHomeDelivery = !isOnPremise && !isPickupPoint && rawMethod && ['home', 'door', 'address', 'area', 'delivery'].some(k => rawMethod.includes(k));
  const deliveryMethodDisplay = isOnPremise
    ? (orderData.delivery_location_name || (rawMethod === 'takeaway' ? 'איסוף עצמי' : 'שירות במקום'))
    : (isPickupPoint
      ? 'נקודת איסוף'
      : (isHomeDelivery ? 'משלוח עד הבית' : (deliveryMethod || '')));
  // Always show address for home delivery, pickup point name for pickup
  let deliveryLocationDisplay = '';
  if (isOnPremise) {
    deliveryLocationDisplay = '';
  } else if (isPickupPoint) {
    deliveryLocationDisplay = orderData.delivery_point_name || '';
  } else if (isHomeDelivery) {
    deliveryLocationDisplay = deliveryAddressString || destination || '';
  } else {
    deliveryLocationDisplay = destination || orderData.delivery_point_name || '';
  }

  // Price calculations fallbacks
  const computedSubtotal = items.reduce((sum, item) => {
    const price = item.price ?? item.unit_price ?? item.cost ?? 0;
    const qty = item.quantity ?? item.qty ?? 1;
    return sum + price * qty;
  }, 0);
  const subtotal = orderData.subtotal != null ? orderData.subtotal : computedSubtotal;
  const discount = orderData.discount || 0;
  // Paid options extraction (broader & resilient) with quantity scaling by parent item quantity
  const topLevelOptionSources = [
    orderData.paid_options,
    orderData.paidOptions,
    orderData.options,
    orderData.selected_options,
    orderData.extra_options,
    orderData.addons,
    orderData.add_ons,
    orderData.extras
  ].filter(Boolean);

  const normalizeOptionsCollection = (src) => {
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (typeof src === 'object') return Object.values(src);
    return [];
  };

  // Aggregate map keyed by name+price to sum quantities across items
  const optionAggregate = new Map();

  const accumulateOption = (opt, multiplier = 1, parentItemName = null) => {
    if (!opt) return;
    const name = opt.name || opt.title || opt.label || opt.id || JSON.stringify(opt);
    if (!name) return;
    const priceVal = opt.price ?? opt.unit_price ?? opt.cost ?? opt.amount ?? opt.value ?? 0;
    const baseQty = opt.quantity ?? opt.qty ?? 1;
    const combinedQty = baseQty * multiplier;
    const key = name + '|' + priceVal;
    if (!optionAggregate.has(key)) {
      optionAggregate.set(key, {
        original: opt,
        name,
        priceVal,
        combinedQuantity: combinedQty,
        itemNames: parentItemName ? new Set([parentItemName]) : new Set(),
      });
    } else {
      const existing = optionAggregate.get(key);
      existing.combinedQuantity += combinedQty;
      if (parentItemName) existing.itemNames.add(parentItemName);
    }
  };

  // Top-level options (not tied to specific items)
  normalizeOptionsCollection(topLevelOptionSources.flatMap(normalizeOptionsCollection)).forEach(opt => accumulateOption(opt, 1));

  // Item-level options with item quantity scaling
  items.forEach(item => {
    const itemQty = item.quantity ?? item.qty ?? 1;
    const itemName = item.product_name || item.name || item.title || 'פריט';
    const optionSources = [
      item.paid_options,
      item.paidOptions,
      item.options,
      item.selected_options,
      item.extra_options,
      item.addons,
      item.add_ons,
      item.extras
    ].filter(Boolean);
    optionSources.flatMap(normalizeOptionsCollection).forEach(opt => accumulateOption(opt, itemQty, itemName));
  });

  // Build final paidOptions array
  const paidOptions = Array.from(optionAggregate.values()).map(entry => {
    const { original, name, priceVal, combinedQuantity, itemNames } = entry;
    return {
      ...original,
      _displayName: name,
      _combinedQuantity: combinedQuantity,
      _price: priceVal,
      _items: Array.from(itemNames),
    };
  });

  const paidOptionsTotal = paidOptions.reduce((sum, opt) => sum + (opt._price * opt._combinedQuantity), 0);

  // Dev-mode debug (avoid referencing process when not polyfilled)
  const isDevEnv = (
    typeof window !== 'undefined' && (
      (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') ||
      (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE !== 'production')
    )
  );
  if (isDevEnv) {
    // eslint-disable-next-line no-console
    console.debug('[OrderConfirmation] paidOptions extracted', { paidOptions, paidOptionsTotal });
  }

  const total = orderData.total != null
    ? orderData.total
    : (subtotal - discount + deliveryFee + paidOptionsTotal);

  const handleDownload = async () => {
    try {
      // Dynamic imports keep initial bundle smaller
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const element = document.getElementById('order-confirmation-root');
      if (!element) return;

      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = canvas.height * imgWidth / canvas.width;

      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // If content taller than one page, scale to fit height
        const scaledHeight = pageHeight - 40; // margin
        const scaledWidth = canvas.width * scaledHeight / canvas.height;
        const x = (pageWidth - scaledWidth) / 2;
        pdf.addImage(imgData, 'PNG', x, 20, scaledWidth, scaledHeight);
      }

      pdf.save(`order-${orderId}.pdf`);
    } catch (e) {
      console.error('PDF download failed', e);
    }
  };

  // Ensure content / buttons aren't hidden behind a fixed mobile footer nav
  const [bottomPadding, setBottomPadding] = React.useState(48); // slightly larger default

  React.useLayoutEffect(() => {
    let resizeObserver;
    const footer = document.getElementById('footer-nav') || document.querySelector('[data-footer-nav]');

    const applyPadding = () => {
      const f = document.getElementById('footer-nav') || document.querySelector('[data-footer-nav]');
      if (f) {
        const h = f.getBoundingClientRect().height;
        // Add breathing room (24px) above footer and consider iOS safe area inset
        setBottomPadding(h + 24);
      } else {
        // Retry in case footer mounted after this component
        setTimeout(() => {
          const late = document.getElementById('footer-nav') || document.querySelector('[data-footer-nav]');
          if (late) {
            const hLate = late.getBoundingClientRect().height;
            setBottomPadding(hLate + 24);
          }
        }, 250);
      }
    };

    applyPadding();
    window.addEventListener('resize', applyPadding);

    if (footer && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(applyPadding);
      resizeObserver.observe(footer);
    }

    return () => {
      window.removeEventListener('resize', applyPadding);
      if (resizeObserver && footer) resizeObserver.unobserve(footer);
    };
  }, []);

  return (
    <div
      id="order-confirmation-root"
      className="p-6 space-y-6 animate-fadein"
      style={{ paddingBottom: `calc(${bottomPadding}px + env(safe-area-inset-bottom, 0px))` }}
    >
      {/* Success Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="bg-success-100 rounded-full p-6 animate-pulse">
            <CheckCircle className="w-16 h-16 text-success-600" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-mekomit-900">{isAppt ? 'התור נקבע בהצלחה!' : 'ההזמנה בוצעה בהצלחה!'}</h2>
        <p className="text-mekomit-700 text-lg">{isAppt ? 'נתראה בקרוב, נשמח לראותך!' : 'תודה רבה על ההזמנה שלך'}</p>
      </div>

      {/* Order Details */}
      <div className="bg-white rounded-2xl border border-mekomit-100 overflow-hidden">
        <div className="bg-mekomit-100 px-6 py-4">
          <h3 className="text-lg font-bold text-mekomit-900 flex items-center gap-2">
            <Package className="w-5 h-5" />
            {isAppt ? 'פרטי התור' : 'פרטי ההזמנה'}
          </h3>
        </div>
        <div className="p-6 space-y-4">
          {/* Customer Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-mekomit-800">
              <span className="font-semibold">שם:</span>
              <span>{customerName}</span>
            </div>
            <div className="flex items-center gap-2 text-mekomit-800">
              <Phone className="w-4 h-4" />
              <span className="font-semibold">טלפון:</span>
              <span className="font-mono">{customerPhone}</span>
            </div>
            {customerEmail && (
              <div className="flex items-center gap-2 text-mekomit-800">
                <Mail className="w-4 h-4" />
                <span className="font-semibold">אימייל:</span>
                <span>{customerEmail}</span>
              </div>
            )}
          </div>

          {/* (Removed separate delivery point block; unified below) */}

          {/* Payment Method */}
          <div className="pt-2 border-t border-mekomit-100">
            <div className="flex items-center gap-2 text-mekomit-800 mb-2">
              <CreditCard className="w-4 h-4" />
              <span className="font-semibold">אמצעי תשלום:</span>
              <span>{paymentMethod}</span>
            </div>
            <div className="bg-info-50 border border-info-100 rounded-lg p-3">
              <p className="text-xs text-mekomit-700">
                <strong>שים לב:</strong> אמצעי התשלום המופיע הוא לצורכי רישום בלבד. התשלום בפועל יתבצע בנפרד ואינו כלול בהזמנה זו.
              </p>
            </div>
          </div>

          {/* Unified Delivery Method & Location */}
          {(deliveryMethodDisplay || deliveryLocationDisplay) && (
            <div className="flex items-start gap-2 text-mekomit-800 pt-2 border-t border-mekomit-100">
              <Truck className="w-4 h-4 mt-1" />
              <div className="space-y-1">
                {deliveryMethodDisplay && (
                  <div>
                    <span className="font-semibold">{isOnPremise ? 'אופן קבלה:' : 'שיטת משלוח:'}</span> <span>{deliveryMethodDisplay}</span>
                  </div>
                )}
                {deliveryLocationDisplay && (
                  <div>
                    <span className="font-semibold">{isPickupPoint ? 'נקודת איסוף:' : 'כתובת למשלוח:'}</span> <span>{deliveryLocationDisplay}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected time slot */}
          {timeSlotDisplay && (
            <div className="flex items-start gap-2 text-mekomit-800 pt-2 border-t border-mekomit-100">
              <Clock className="w-4 h-4 mt-1" />
              <div>
                <span className="font-semibold">מועד איסוף:</span> <span>{timeSlotDisplay}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          {orderData.notes && (
            <div className="flex items-start gap-2 text-mekomit-800 pt-2 border-t border-mekomit-100">
              <FileText className="w-4 h-4 mt-1" />
              <div className="flex-1">
                <span className="font-semibold">הערות:</span>
                <p className="text-sm mt-1">{orderData.notes}</p>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="pt-2 border-t border-mekomit-100">
            <p className="font-semibold text-mekomit-900 mb-3">{isAppt ? 'השירות שנקבע:' : 'פריטים בהזמנה:'}</p>
            <div className="space-y-2">
              {items && items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-mekomit-800">
                    {(item.product_name || item.name || item.title || 'פריט')} × {(item.quantity ?? item.qty ?? 1)}
                  </span>
                  <span className="font-semibold text-mekomit-900">
                    ₪{(((item.price ?? item.unit_price ?? item.cost ?? 0) * (item.quantity ?? item.qty ?? 1))).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Paid Options */}
          {paidOptions.length > 0 && (
            <div className="pt-2 border-t border-mekomit-100">
              <p className="flex items-center gap-2 font-semibold text-mekomit-900 mb-3">
                <Tag className="w-4 h-4" /> אפשרויות בתשלום:
              </p>
              <div className="space-y-2">
                {paidOptions.map((opt, i) => {
                  const combinedQty = opt._combinedQuantity ?? (opt.quantity ?? opt.qty ?? 1);
                  const price = opt._price ?? (opt.price ?? opt.unit_price ?? opt.cost ?? opt.amount ?? opt.value ?? 0);
                  const lineTotal = price * combinedQty;
                  const itemContext = opt._items && opt._items.length > 0 ? ` (לפריט: ${opt._items.join(', ')})` : '';
                  return (
                    <div key={i} className="flex justify-between text-xs sm:text-sm">
                      <span className="text-mekomit-800">
                        {(opt._displayName || opt.name || opt.title || opt.label || 'אפשרות')}{itemContext} {combinedQty > 1 && `× ${combinedQty}`}
                      </span>
                      <span className="font-semibold text-mekomit-900">₪{lineTotal.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="pt-3 border-t-2 border-mekomit-200">
            {subtotal > 0 && (
              <div className="flex justify-between text-base mb-2">
                <span className="text-mekomit-700">סכום ביניים:</span>
                <span className="font-semibold text-mekomit-900">₪{subtotal.toFixed(2)}</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-base text-success-600 mb-2">
                <span>הנחה:</span>
                <span className="font-semibold">-₪{discount.toFixed(2)}</span>
              </div>
            )}
            {/* {paidOptionsTotal > 0 && (
              <div className="flex justify-between text-base mb-2">
                <span className="text-mekomit-700">אפשרויות בתשלום:</span>
                <span className="font-semibold text-mekomit-900">₪{paidOptionsTotal.toFixed(2)}</span>
              </div>
            )} */}
            {deliveryFee > 0 && (
              <div className="flex justify-between text-base mb-2">
                <span className="text-mekomit-700">דמי משלוח:</span>
                <span className="font-semibold text-mekomit-900">₪{deliveryFee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold text-mekomit-900">
              <span>סה"כ:</span>
              <span>₪{(total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 sticky bottom-[calc(env(safe-area-inset-bottom,0px)+${bottomPadding - 24}px)] bg-white/70 backdrop-blur-sm pt-2 rounded-xl">
        <button
          onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 bg-white border border-mekomit-300 text-mekomit-800 font-semibold py-3 rounded-xl hover:bg-mekomit-50 transition-all"
          type="button"
        >
          <Download className="w-5 h-5" />
          הורד סיכום PDF
        </button>
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-mekomit-500 to-mekomit-400 hover:from-mekomit-600 hover:to-mekomit-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg"
          type="button"
        >
          סגור
        </button>
      </div>
      {/* Spacer to guarantee scroll clearance above fixed footer */}
      <div aria-hidden className="pointer-events-none" style={{ height: bottomPadding }} />
    </div>
  );
};
